import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BUSINESS_REGION_GROUPS,
  BUSINESS_REGION_METRIC_LABEL,
  BUSINESS_REGION_UNKNOWN,
  businessRegionGroupOf,
  emptyBusinessRegionValues,
  type BusinessRegionMetric,
  type BusinessRegionRow,
  type BusinessRegionValues,
  type BusinessRegionsResponse,
  type SetBusinessRegionInputRequest,
} from '@bhops/shared';
import { teamDashboardApi } from '@/api/team-dashboard';
import { extractApiError } from '@/api/client';
import { useToast } from '@/components/Toast';
import type { IconName } from '@/components/Icon';
import { kstYmd } from '@/lib/format-date';
import { HorizontalBars, IconLabel, SHARED_EMPTY_HINT, Section } from './_shared';
import { KoreaRegionMap } from './KoreaRegionMap';

// 2026-10-26 오너 — 사업팀 엑셀 '권역별현황, 계약현황'을 대시보드로 옮겼다. 숫자는 서버가 콘솔 데이터로
// 세고(business-regions.service), 사람은 달마다 목표치를 넣고 시도별 칸을 보정할 수 있다.

const COLOR: Record<'ib' | 'demo' | 'contracts' | 'live', string> = {
  ib: 'var(--robot-shipping)',
  demo: 'var(--robot-installing)',
  contracts: 'var(--brand)',
  live: 'var(--robot-maintenance)',
};

/** 목표 카드에 놓는 지표(설치 매장은 표에만) */
const TARGET_METRICS: BusinessRegionMetric[] = [
  'ib',
  'demo',
  'contracts',
  'contractUnits',
  'liveUnits',
];
// 2026-10-27 오너 "앞에 아이콘들 적절하게 배치하자. 아이콘에 색도 넣고" — 카드 이름 앞의 색 아이콘.
// 바탕은 같은 색을 옅게 섞어 밝은 · 어두운 화면 모두에서 읽힌다(패치노트 아이콘과 같은 모양).
const CARD_ICON: Record<string, { icon: IconName; color: string }> = {
  ib: { icon: 'message', color: '#2f6fe0' },
  demo: { icon: 'eye', color: '#7c4dff' },
  contracts: { icon: 'signature', color: '#0f766e' },
  contractUnits: { icon: 'bot', color: '#d97706' },
  liveUnits: { icon: 'map-pin', color: '#16a34a' },
  conversion: { icon: 'refresh', color: '#db2777' },
};

function CardLabel({ kind, children }: { kind: string; children: ReactNode }) {
  const { icon, color } = CARD_ICON[kind];
  return (
    <IconLabel icon={icon} color={color}>
      {children}
    </IconLabel>
  );
}

const UNIT: Record<BusinessRegionMetric, string> = {
  ib: '건',
  demo: '건',
  contracts: '건',
  contractUnits: '대',
  liveStores: '곳',
  liveUnits: '대',
};

function monthLabel(m: string): string {
  return `${m.slice(0, 4)}년 ${Number(m.slice(5, 7))}월`;
}

function pct(n: number, total: number): string {
  if (!total) return '–';
  return `${Math.round((n / total) * 1000) / 10}%`;
}

function sumRows(rows: BusinessRegionRow[]): BusinessRegionValues {
  const s = emptyBusinessRegionValues();
  for (const r of rows)
    for (const k of Object.keys(s) as BusinessRegionMetric[]) s[k] += r.value[k];
  return s;
}

export function BusinessRegionsSection() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  // 2026-10-27 오너 "권역별 현황에는 목표 입력만 두고, 권역별 표에다가 보정 입력을 두자" — 입력 단추를 둘로.
  const [editingTargets, setEditingTargets] = useState(false);
  const [editingOverrides, setEditingOverrides] = useState(false);
  // 지도와 권역별 표가 함께 쓰는 '고른 시도'.
  const [selectedSido, setSelectedSido] = useState<string | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({
    queryKey: ['dashboard', 'business-regions', month ?? 'now'],
    queryFn: () => teamDashboardApi.businessRegions(month),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: (body: SetBusinessRegionInputRequest) =>
      teamDashboardApi.setBusinessRegionInput(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'business-regions'] }),
    onError: (err) => toast.error(extractApiError(err), '저장하지 못했습니다'),
  });

  const d = q.data;
  // 2026-10-27 오너 "월 선택하는 버튼은 권역별 현황 텍스트 옆에 두자" — 달 선택은 제목 옆, 목표 입력은 오른쪽 끝.
  const monthPicker = d && (
    <select
      aria-label="달 선택"
      value={d.month}
      onChange={(e) => setMonth(e.target.value)}
      style={selectStyle}
    >
      {d.months.map((m) => (
        <option key={m} value={m}>
          {monthLabel(m)}
        </option>
      ))}
    </select>
  );
  const picker = d && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {d.canEdit && (
        <button
          type="button"
          data-testid="regions-target-toggle"
          onClick={() => setEditingTargets((v) => !v)}
          style={editingTargets ? { ...toggleBtn, ...toggleOn } : toggleBtn}
        >
          {editingTargets ? '입력 끝내기' : '목표 입력'}
        </button>
      )}
    </div>
  );

  if (q.isLoading) {
    return (
      <Section title="권역별 현황">
        <div style={SHARED_EMPTY_HINT}>불러오는 중…</div>
      </Section>
    );
  }
  if (q.error || !d) {
    return (
      <Section title="권역별 현황">
        <div style={{ color: 'var(--fg-danger)', fontSize: 12 }}>
          권역별 현황을 불러오지 못했습니다. {q.error ? extractApiError(q.error) : ''}
        </div>
      </Section>
    );
  }

  const setInput = (
    kind: 'TARGET' | 'OVERRIDE',
    metric: BusinessRegionMetric,
    region: string,
    value: number | null,
  ) => save.mutate({ month: d.month, kind, metric, region, value });

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      data-testid="business-regions"
    >
      {/* 2026-10-27 오너 "이런식으로 가야지"(그림: 권역별 현황 큰 칸 안에 표 · 지도 상자) — 숫자 카드와
          표 · 지도를 한 칸에 묶는다. 달은 오른쪽 위 달 선택에 보이므로 제목에서 뺐다. */}
      <Section title="권역별 현황" titleExtra={monthPicker} extra={picker}>
        <TargetCards
          d={d}
          editing={editingTargets}
          onSetTarget={(m, v) => setInput('TARGET', m, '', v)}
        />
        {/* 2026-10-27 오너 "이런식으로 하는게 어때" — 표는 왼쪽에 내용만큼, 오른쪽에는 지도. 창이 좁으면 지도가 표 아래로 내려간다. */}
        {/* 2026-10-27 오너 "이거 둘이 높이 맞춰줘" — 표 칸과 지도 칸은 같은 줄에 서면 키가 같다(stretch).
          지도는 남는 높이를 채운다. */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'stretch',
            // 숫자 카드와 표 · 지도 사이 — 2026-10-27 오너 "여기 간격을 넓히자".
            marginTop: 48,
          }}
        >
          {/* min-content — 표 너비에 맞추고, 아래 출처 문장은 그 폭 안에서 줄바꿈한다. */}
          <div style={{ ...equalCol, flex: '0 1 auto', width: 'min-content', maxWidth: '100%' }}>
            <Section
              style={{ ...innerBox, flex: 1 }}
              title="권역별 표"
              extra={
                d.canEdit && (
                  <button
                    type="button"
                    data-testid="regions-override-toggle"
                    onClick={() => setEditingOverrides((v) => !v)}
                    style={editingOverrides ? { ...toggleBtn, ...toggleOn } : toggleBtn}
                  >
                    {editingOverrides ? '입력 끝내기' : '보정 입력'}
                  </button>
                )
              }
            >
              <RegionTable
                d={d}
                editing={editingOverrides}
                onOverride={(metric, region, v) => setInput('OVERRIDE', metric, region, v)}
                selected={selectedSido}
                onSelect={setSelectedSido}
              />
              <SourceNote d={d} />
            </Section>
          </div>
          <div style={{ ...equalCol, flex: '1 1 340px' }}>
            {/* 2026-10-27 오너 "옆에 지도만 있는게 더 낫네" — 표 옆에는 지도만, 그래프는 아래 칸으로. */}
            <Section
              title="지도로 보기"
              style={{ ...innerBox, flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <KoreaRegionMap rows={d.rows} selected={selectedSido} onSelect={setSelectedSido} />
            </Section>
          </div>
        </div>
        {/* 2026-10-27 오너 "그 안에 권역별 문의, 데모, 계약, 권역별 설치 대수 이런거도 넣어야돼. 표랑 지도
            아래쪽에" — 그래프들도 같은 큰 칸 안, 표 · 지도 아래에. */}
        <RegionCharts d={d} />
      </Section>
    </div>
  );
}

function TargetCards({
  d,
  editing,
  onSetTarget,
}: {
  d: BusinessRegionsResponse;
  editing: boolean;
  onSetTarget: (m: BusinessRegionMetric, v: number | null) => void;
}) {
  const total = sumRows(d.rows);
  const demoRate = pct(total.demo, total.ib);
  const contractRate = pct(total.contracts, total.demo);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 8,
        // 위 달 선택 · 입력 단추와 붙지 않게(2026-10-27 오너 "간격 좀 띄워").
        marginTop: 12,
      }}
    >
      {TARGET_METRICS.map((m) => {
        const actual = total[m];
        const target = d.targets[m];
        const ratio = target ? Math.min(actual / target, 1) : 0;
        const done = target != null && actual >= target;
        return (
          <div key={m} style={targetCard} data-testid={`regions-target-${m}`}>
            <CardLabel kind={m}>{BUSINESS_REGION_METRIC_LABEL[m]}</CardLabel>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
              {actual.toLocaleString()}
              <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 2 }}>{UNIT[m]}</span>
            </div>
            {editing ? (
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                목표
                <NumberInput
                  value={target ?? null}
                  ariaLabel={`${BUSINESS_REGION_METRIC_LABEL[m]} 목표`}
                  onSave={(v) => onSetTarget(m, v)}
                />
              </label>
            ) : target != null ? (
              <>
                <div style={barTrack}>
                  <div
                    style={{
                      ...barFill,
                      width: `${ratio * 100}%`,
                      background: done ? 'var(--fg-success, var(--brand))' : 'var(--brand)',
                    }}
                  />
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  목표 {target.toLocaleString()}
                  {UNIT[m]}, 달성률 {pct(actual, target)}
                </div>
              </>
            ) : null}
          </div>
        );
      })}
      <div style={targetCard}>
        <CardLabel kind="conversion">전환율</CardLabel>
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          문의 → 데모 <b>{demoRate}</b>
          <br />
          데모 → 계약 <b>{contractRate}</b>
        </div>
      </div>
    </div>
  );
}

function RegionCharts({ d }: { d: BusinessRegionsResponse }) {
  const byGroup = BUSINESS_REGION_GROUPS.map((g) => {
    const s = sumRows(d.rows.filter((r) => r.group === g.group));
    return { group: g.group, 문의: s.ib, 데모: s.demo, 계약: s.contracts, 설치: s.liveUnits };
  });
  const total = sumRows(d.rows);
  const ibBars = d.rows
    .filter((r) => r.value.ib > 0)
    .sort((a, b) => b.value.ib - a.value.ib)
    .slice(0, 10)
    .map((r) => ({
      key: r.region,
      label: `${r.region} ${pct(r.value.ib, total.ib)}`,
      count: r.value.ib,
      title: `${r.region}: 문의 ${r.value.ib}건, 전체의 ${pct(r.value.ib, total.ib)}`,
      color: COLOR.ib,
    }));
  const trend = d.trend.map((t) => ({
    month: `${Number(t.month.slice(5, 7))}월`,
    문의: t.ib,
    데모: t.demo,
    계약: t.contracts,
    설치: t.liveUnits,
  }));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
        gap: 16,
        marginTop: 48,
      }}
    >
      <Section title="권역별 문의, 데모, 계약" style={innerBox}>
        <div style={{ width: '100%', height: 220 }} data-testid="regions-chart-funnel">
          <ResponsiveContainer>
            <BarChart data={byGroup} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid
                stroke="var(--border-default)"
                strokeDasharray="2 4"
                vertical={false}
              />
              <XAxis dataKey="group" tick={{ fontSize: 12, fill: 'var(--fg-muted)' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--fg-muted)' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
              <Bar dataKey="문의" fill={COLOR.ib} radius={[3, 3, 0, 0]} />
              <Bar dataKey="데모" fill={COLOR.demo} radius={[3, 3, 0, 0]} />
              <Bar dataKey="계약" fill={COLOR.contracts} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
      <Section title="월별 추이 (최근 6개월)" style={innerBox}>
        <div style={{ width: '100%', height: 220 }} data-testid="regions-chart-trend">
          <ResponsiveContainer>
            <LineChart data={trend} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
              <CartesianGrid stroke="var(--border-default)" strokeDasharray="2 4" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--fg-muted)' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--fg-muted)' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
              <Line type="monotone" dataKey="문의" stroke={COLOR.ib} strokeWidth={2} />
              <Line type="monotone" dataKey="데모" stroke={COLOR.demo} strokeWidth={2} />
              <Line type="monotone" dataKey="계약" stroke={COLOR.contracts} strokeWidth={2} />
              <Line type="monotone" dataKey="설치" stroke={COLOR.live} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>
      <Section title="시도별 문의 (상위 10곳)" style={innerBox}>
        <HorizontalBars items={ibBars} emptyText="이 달 문의가 없습니다." />
      </Section>
      <Section title="권역별 설치 대수" style={innerBox}>
        <HorizontalBars
          items={byGroup
            .filter((g) => g.설치 > 0)
            .map((g) => ({
              key: g.group,
              label: g.group,
              count: g.설치,
              title: `${g.group}: ${g.설치}대`,
              color: COLOR.live,
            }))}
          emptyText="설치된 로봇이 없습니다."
        />
      </Section>
    </div>
  );
}

// 2026-10-27 오너 "묶인 것들끼리 명확하게 보여야해 — 문의-비율, 데모-비율, 계약-비율-대수". 머리글을 두 줄로
// 나눠 위 줄에 묶음 이름을 걸고, 묶음 사이에는 굵은 세로줄을 긋는다. 비율은 전국 합계 대비.
type TableCol = { metric: BusinessRegionMetric; label: string; share?: boolean };
const TABLE_GROUPS: Array<{ label: string; cols: TableCol[] }> = [
  {
    label: '문의',
    cols: [
      { metric: 'ib', label: '건수' },
      { metric: 'ib', label: '비율', share: true },
    ],
  },
  {
    label: '데모',
    cols: [
      { metric: 'demo', label: '건수' },
      { metric: 'demo', label: '비율', share: true },
    ],
  },
  {
    label: '계약',
    cols: [
      { metric: 'contracts', label: '건수' },
      { metric: 'contracts', label: '비율', share: true },
      { metric: 'contractUnits', label: '대수' },
    ],
  },
  {
    label: '설치',
    cols: [
      { metric: 'liveStores', label: '매장' },
      { metric: 'liveStores', label: '비율', share: true },
      { metric: 'liveUnits', label: '대수' },
    ],
  },
];

function RegionTable({
  d,
  editing,
  onOverride,
  selected,
  onSelect,
}: {
  d: BusinessRegionsResponse;
  editing: boolean;
  onOverride: (metric: BusinessRegionMetric, region: string, v: number | null) => void;
  /** 2026-10-27 오너 "지도에서 지역 선택하면 표도 해당 지역 파트 하이라이트" — 지도와 함께 쓰는 선택. */
  selected: string | null;
  onSelect: (sido: string | null) => void;
}) {
  const selectedGroup = selected ? businessRegionGroupOf(selected) : null;
  // 입력 중에는 줄을 눌러도 선택하지 않는다(칸을 고치려고 누른 것).
  const rowProps = (region: string) => ({
    'data-selected': selected === region ? 'true' : undefined,
    onClick: editing ? undefined : () => onSelect(selected === region ? null : region),
    style: {
      cursor: editing ? undefined : 'pointer',
      ...(selected === region ? selectedRow : null),
    } as CSSProperties,
  });
  const total = sumRows(d.rows);
  const unknown = d.rows.find((r) => r.region === BUSINESS_REGION_UNKNOWN);

  const valueCells = (v: BusinessRegionValues, row?: BusinessRegionRow, top?: CSSProperties) =>
    TABLE_GROUPS.flatMap((g) =>
      g.cols.map(({ metric, share }, ci) => {
        const edge = { ...(ci === 0 ? groupEdge : null), ...top };
        if (share) {
          return (
            <td key={`${metric}-share`} style={{ ...td, ...edge, color: 'var(--fg-muted)' }}>
              {pct(v[metric], total[metric])}
            </td>
          );
        }
        const overridden = row && row.override[metric] != null;
        return (
          <td
            key={metric}
            style={{ ...td, ...edge, ...(overridden ? overriddenCell : null) }}
            title={
              overridden
                ? `직접 고친 값입니다. 자동으로 센 값은 ${row!.auto[metric]}입니다.`
                : undefined
            }
            data-testid={row ? `regions-cell-${row.region}-${metric}` : undefined}
          >
            {editing && row ? (
              <NumberInput
                value={v[metric]}
                ariaLabel={`${row.region} ${BUSINESS_REGION_METRIC_LABEL[metric]}`}
                onSave={(n) => onOverride(metric, row.region, n === row.auto[metric] ? null : n)}
              />
            ) : (
              v[metric].toLocaleString()
            )}
          </td>
        );
      }),
    );

  return (
    // 바깥 테두리는 감싼 상자가 긋는다 — 표가 그으면 상자의 둥근 모서리에 선이 깎여 보였다
    // (2026-10-27 오너 "표들 모서리 이런식으로 잘리는거"). 표 바깥선은 hidden 으로 끈다.
    <div style={{ overflowX: 'auto', border: CELL_BORDER, width: 'fit-content', maxWidth: '100%' }}>
      {/* data-plain-table — 머리글 칸 사이의 짧은 구분선(globals.css)을 긋지 않는 보통 격자 표. */}
      <table style={table} data-testid="regions-table" data-plain-table="">
        <thead>
          <tr>
            <th style={th} rowSpan={2}>
              권역
            </th>
            <th style={{ ...th, ...sidoEdge }} rowSpan={2}>
              시도
            </th>
            {TABLE_GROUPS.map((g) => (
              <th
                key={g.label}
                colSpan={g.cols.length}
                style={{ ...th, ...groupEdge, ...groupHead }}
                data-testid={`regions-head-${g.label}`}
              >
                {g.label}
              </th>
            ))}
          </tr>
          <tr>
            {TABLE_GROUPS.flatMap((g) =>
              g.cols.map((c, ci) => (
                <th
                  key={`${g.label}-${c.label}`}
                  style={{ ...th, ...(ci === 0 ? groupEdge : null), fontWeight: 500 }}
                >
                  {c.label}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {BUSINESS_REGION_GROUPS.map((g) => {
            const rows = d.rows.filter((r) => r.group === g.group);
            const single = g.sidos.length === 1;
            return [
              ...rows.map((r, i) => {
                // 권역(수도권 · 경상권 …)이 바뀌는 줄 위에는 진한 가로줄.
                const top = i === 0 ? regionTop : undefined;
                return (
                  <tr
                    key={r.region}
                    data-testid={`regions-row-${r.region}`}
                    {...rowProps(r.region)}
                  >
                    {i === 0 && (
                      <td
                        style={{
                          ...td,
                          ...groupCell,
                          ...top,
                          ...(selectedGroup === g.group ? selectedGroupCell : null),
                        }}
                        rowSpan={single ? 1 : rows.length + 1}
                      >
                        {g.group}
                      </td>
                    )}
                    <td style={{ ...td, ...sidoEdge, ...top, ...sidoCell }}>
                      {single ? '' : r.region}
                    </td>
                    {valueCells(r.value, r, top)}
                  </tr>
                );
              }),
              !single && (
                <tr key={`${g.group}-sum`} style={subtotalRow}>
                  <td style={{ ...td, ...sidoEdge, textAlign: 'left' }}>계</td>
                  {valueCells(sumRows(rows))}
                </tr>
              ),
            ];
          })}
          {unknown && (
            <tr data-testid={`regions-row-${unknown.region}`} {...rowProps(unknown.region)}>
              <td style={{ ...td, ...groupCell, ...regionTop }} colSpan={2}>
                미확인
              </td>
              {valueCells(unknown.value, unknown, regionTop)}
            </tr>
          )}
          <tr style={totalRow}>
            <td style={{ ...td, ...groupCell, ...regionTop }} colSpan={2}>
              총계
            </td>
            {valueCells(total, undefined, regionTop)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function SourceNote({ d }: { d: BusinessRegionsResponse }) {
  const asOf = kstYmd(d.sources.liveAsOf);
  return (
    <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.7 }}>
      문의는 구글 시트 {d.sources.ibTab ? `'${d.sources.ibTab}'` : '도입문의 현황'} 탭
      {d.sources.ibTab ? '' : '(이 달 탭이 아직 콘솔에 없습니다)'}의 지역2 칸(비어 있으면
      지역1)으로, 데모는 '{d.sources.demoTab ?? '데모-계약 전환'}' 탭에서 시연 날짜가 이 달인 줄로
      셉니다. 계약은 콘솔 계약의 계약일 기준이고, 설치는 {asOf} 기준 고객 매장에 있는
      로봇입니다(스페어 제외). 노란 칸은 직접 고친 값입니다.
    </div>
  );
}

/** 숫자 칸 — Enter 나 칸을 벗어나면 저장, 비우면 지운다. */
function NumberInput({
  value,
  ariaLabel,
  onSave,
}: {
  value: number | null;
  ariaLabel: string;
  onSave: (v: number | null) => void;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => setText(value == null ? '' : String(value)), [value]);
  const commit = () => {
    const t = text.trim();
    const next = t === '' ? null : Number(t);
    if (next != null && (!Number.isInteger(next) || next < 0)) {
      setText(value == null ? '' : String(value));
      return;
    }
    if (next !== value) onSave(next);
  };
  return (
    <input
      type="number"
      min={0}
      aria-label={ariaLabel}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      style={numInput}
    />
  );
}

const selectStyle: CSSProperties = {
  height: 30,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
// 2026-10-27 오너 "목표 입력이랑 보정 입력은 잘 보이게 채도 약한 색을 넣자" — 꺼진 단추도 브랜드 색을
// 옅게 깔아 눈에 띄게. 켜지면(toggleOn) 진한 브랜드 색.
const toggleBtn: CSSProperties = {
  ...selectStyle,
  padding: '0 12px',
  cursor: 'pointer',
  fontWeight: 600,
  border: '1px solid color-mix(in srgb, var(--brand) 30%, var(--bg-surface))',
  background: 'color-mix(in srgb, var(--brand) 10%, var(--bg-surface))',
  color: 'var(--fg-brand, var(--brand))',
};
const toggleOn: CSSProperties = {
  background: 'var(--brand)',
  color: 'var(--brand-on)',
  // borderColor 만 얹으면 꺼질 때 검은 테두리가 남는다 — shorthand 로 통째 교체.
  border: '1px solid var(--brand)',
};
// 2026-10-27 오너 "이거도 너무 커" — 대시보드 숫자 카드(KpiCard)와 같은 크기 · 모양.
/** 권역별 현황 큰 칸 안의 표 · 지도 상자 — 테두리만, 그림자 없이. */
// 2026-10-27 오너 "여기 구분 선 색 좀 진하게" — 권역별 현황 안 카드 · 상자 테두리. --border-strong 도 흐려서
// 글자색을 섞어 진하게 한다(어두운 화면에서는 밝은 선이 된다).
const CARD_LINE = '1px solid var(--border-card)';
const innerBox: CSSProperties = { boxShadow: 'none', border: CARD_LINE };
const targetCard: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 6,
  // 2026-10-27 오너 "이건 기존처럼 흰색으로 해도 되고" — 바탕은 흰색, 크기만 작게.
  border: CARD_LINE,
  background: 'var(--bg-surface)',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const barTrack: CSSProperties = {
  height: 6,
  borderRadius: 3,
  background: 'var(--bg-subtle, var(--border-default))',
  overflow: 'hidden',
};
const barFill: CSSProperties = { height: '100%', borderRadius: 3 };
// 2026-10-27 오너 "표 간격 쓸데없이 너무 넓다" — 카드 폭에 맞춰 늘리지 않고 내용만큼만.
// 2026-10-27 오너 "그냥 권역별 표는 일반적인 표의 형태로" — 모든 칸에 얇은 테두리를 긋는 격자 표.
// 선 색은 진하게(2026-10-27 오너 "선 색도 좀 진하게").
const CELL_BORDER = '1px solid var(--border-strong)';
// 본문 칸 선은 연하게(2026-10-28 오너 "여기도 좀 연하게, 헤더들은 제외") — 머리글 칸 · 굵은 선 · 상자 테두리는 그대로.
const BODY_CELL_BORDER = '1px solid var(--border-subtle)';
const table: CSSProperties = {
  width: 'auto',
  borderCollapse: 'collapse',
  border: 'hidden',
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
};
const th: CSSProperties = {
  padding: '6px 12px',
  border: CELL_BORDER,
  // 머리글 글씨도 진하게(오너 "권역, 시도 글씨도 진한 색으로").
  color: 'var(--fg-default)',
  fontWeight: 600,
  fontSize: 12,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
// 본문 칸은 왼쪽 · 위 선만 긋는다 — 겹친 선(border-collapse)은 같은 굵기면 왼쪽 · 위 칸 것이 이겨서, 칸마다 네 변을
// 그으면 왼쪽 칸의 연한 오른쪽 선이 진한 구분선(groupEdge · sidoEdge · regionTop)을 덮었다. 오른쪽 · 아래가 없으면
// 그 자리는 다음 칸의 선이 차지한다.
const td: CSSProperties = {
  padding: '4px 12px',
  borderLeft: BODY_CELL_BORDER,
  borderTop: BODY_CELL_BORDER,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
const sidoCell: CSSProperties = { textAlign: 'left', fontWeight: 600, color: 'var(--fg-default)' };
const groupCell: CSSProperties = {
  textAlign: 'left',
  fontWeight: 700,
  color: 'var(--fg-default)',
  verticalAlign: 'top',
};
const equalCol: CSSProperties = { display: 'flex', flexDirection: 'column', minWidth: 0 };
// 진한 선 — 2026-10-27 오너 "나누는 선은 진하게 그대로 둬", "권역이랑 시도도 진하게",
// "수도권 경상권 이런거 나누는거도 진한선으로". 묶음(문의 · 데모 · 계약 · 설치) 사이, 권역 칸과 시도 칸 사이,
// 권역이 바뀌는 줄 위.
// 2026-10-28 오너 "두껍게 한 선들은 그냥 이정도 느낌으로" · "이런 선을 구분선으로" — 머리글 칸 선과 같은 1px 진한 선.
const THICK_LINE = CELL_BORDER;
const groupEdge: CSSProperties = { borderLeft: THICK_LINE };
const sidoEdge: CSSProperties = { borderLeft: THICK_LINE };
const regionTop: CSSProperties = { borderTop: THICK_LINE };
const groupHead: CSSProperties = { color: 'var(--fg-default)', fontWeight: 700 };
const selectedRow: CSSProperties = {
  background: 'color-mix(in srgb, var(--section-accent, var(--brand)) 20%, var(--bg-surface))',
  fontWeight: 700,
};
const selectedGroupCell: CSSProperties = {
  background: 'color-mix(in srgb, var(--section-accent, var(--brand)) 9%, var(--bg-surface))',
};
const subtotalRow: CSSProperties = {
  background: 'var(--bg-subtle, var(--bg-surface))',
  fontWeight: 600,
};
const totalRow: CSSProperties = { background: 'var(--bg-info-subtle)', fontWeight: 700 };
const overriddenCell: CSSProperties = { background: 'var(--bg-warning-subtle)' };
const numInput: CSSProperties = {
  width: 64,
  height: 26,
  padding: '0 6px',
  border: '1px solid var(--border-default)',
  borderRadius: 4,
  textAlign: 'right',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};
